import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { IconPicker } from "../IconPicker";
import { ThumbnailColorPicker } from "../ThumbnailColorPicker";
import { ThumbnailDetailsForm } from "../ThumbnailDetailsForm";
import {
  THUMBNAIL_LAYOUT,
  wrapText,
  truncateText,
  createFontString,
  measureCSSRenderedElement,
} from "./thumbnailLayout";
import ThumbnailSvgRenderer from "./ThumbnailSvgRenderer";
import styles from "./ThumbnailBuilder.module.css";

import DRIcon from "shared/assets/ThumbnailColorIcons/DR_Icon.svg";
import DSIcon from "shared/assets/ThumbnailColorIcons/DS_Icon.svg";
import DNIcon from "shared/assets/ThumbnailColorIcons/DN_Icon.svg";
import ERIcon from "shared/assets/ThumbnailColorIcons/ER_Icon.svg";
import FRIcon from "shared/assets/ThumbnailColorIcons/FR_Icon.svg";
import FMAIcon from "shared/assets/ThumbnailColorIcons/FMA_Icon.svg";
import FEIcon from "shared/assets/ThumbnailColorIcons/FE_Selected_Icon.svg";
import SPIcon from "shared/assets/ThumbnailColorIcons/SP_Icon.svg";
import SLIcon from "shared/assets/ThumbnailColorIcons/SL_Icon.svg";
import ThreePIcon from "shared/assets/ThumbnailColorIcons/3P_Icon.svg";

const ICON_META = {
  DR: { src: DRIcon, color: "#000000" },
  DS: { src: DSIcon, color: "#62646A" },
  DN: { src: DNIcon, color: "#4C4F53" },
  ER: { src: ERIcon, color: "#007A60" },
  FR: { src: FRIcon, color: "#B81D28" },
  FMA: { src: FMAIcon, color: "#EA6217" },
  FE: { src: FEIcon, color: "#8B1A1D" },
  SP: { src: SPIcon, color: "#7B8981" },
  SL: { src: SLIcon, color: "#457CBF" },
  "3P": { src: ThreePIcon, color: "#6998AD" },
};

const DEFAULT_STATE = {
  selected: "",
  year: "",
  useIconColor: true,
  color: "#FFFFFF",
  useGradient: false,
  gradient: {
    angle: 40,
    colors: ["#FFFFFF", "#243746", ""],
  },
  details: {
    title: "",
    creatorSpeaker: "",
    topic: "",
    sku: "",
    naCrNumber: "",
    anzCrNumber: "",
  },
};

function normalizeHex(value) {
  if (!value) {
    return "";
  }

  const normalized = value.trim().toUpperCase();
  return /^#([0-9A-F]{6}|[0-9A-F]{8})$/.test(normalized) ? normalized : "";
}

function getRelativeLuminance(hex) {
  const normalized = normalizeHex(hex).slice(0, 7);
  if (!normalized) {
    return 0;
  }

  const channels = [1, 3, 5].map(
    (index) => parseInt(normalized.slice(index, index + 2), 16) / 255,
  );

  const linearized = channels.map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
  );

  return (
    0.2126 * linearized[0] + 0.7152 * linearized[1] + 0.0722 * linearized[2]
  );
}

function mergeInitialData(initialData) {
  if (!initialData) {
    return DEFAULT_STATE;
  }

  const detailsFromInitial = initialData.details || {
    title: initialData.title,
    creatorSpeaker: initialData.creatorSpeaker,
    topic: initialData.topic,
    sku: initialData.sku,
    naCrNumber: initialData.naCrNumber,
    anzCrNumber: initialData.anzCrNumber,
  };

  return {
    ...DEFAULT_STATE,
    ...initialData,
    details: {
      ...DEFAULT_STATE.details,
      ...detailsFromInitial,
    },
    gradient: {
      ...DEFAULT_STATE.gradient,
      ...(initialData.gradient || {}),
      colors: [
        initialData.gradient?.colors?.[0] || DEFAULT_STATE.gradient.colors[0],
        initialData.gradient?.colors?.[1] || DEFAULT_STATE.gradient.colors[1],
        initialData.gradient?.colors?.[2] || DEFAULT_STATE.gradient.colors[2],
      ],
    },
  };
}

function rasterizeSvg(svgString) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = async () => {
      if (document.fonts?.ready) {
        await document.fonts.ready;
        await Promise.all([
          document.fonts.load('300 19px "Open Sans"'),
          document.fonts.load('400 19px "Open Sans"'),
        ]);
      }

      const sourceWidth =
        image.naturalWidth || image.width || THUMBNAIL_LAYOUT.width;
      const sourceHeight =
        image.naturalHeight || image.height || THUMBNAIL_LAYOUT.height;
      const rasterScale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = sourceWidth * rasterScale;
      canvas.height = sourceHeight * rasterScale;

      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Unable to render thumbnail preview."));
        return;
      }

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => reject(new Error("Unable to load thumbnail SVG."));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      svgString,
    )}`;
  });
}

export const ThumbnailBuilder = React.forwardRef(function ThumbnailBuilder(
  {
    initialData,
    onDelete,
    onDetailsChange,
    onDraftChange,
    hasExistingSvg,
    className,
    detailFields,
    readOnlyDetailFields,
    readOnlyDetailsHint,
    syncedDetails,
  },
  ref,
) {
  // Details the host form suggests (e.g. a disclosure's title). They prefill
  // empty fields and keep following the host while the field still matches
  // the last suggestion; once the user types something different, their
  // value is kept.
  const lastSyncedRef = React.useRef(syncedDetails || {});
  const [state, setState] = useState(() => {
    const merged = mergeInitialData(initialData);
    if (!syncedDetails) return merged;
    const details = { ...merged.details };
    Object.entries(syncedDetails).forEach(([field, value]) => {
      if (!details[field]) details[field] = value || "";
    });
    return { ...merged, details };
  });

  const syncedKey = JSON.stringify(syncedDetails || null);
  React.useEffect(() => {
    if (!syncedDetails) return;
    const previous = lastSyncedRef.current || {};
    lastSyncedRef.current = syncedDetails;
    setState((current) => {
      let changed = false;
      const details = { ...current.details };
      Object.entries(syncedDetails).forEach(([field, value]) => {
        const currentValue = details[field] || "";
        const followsHost =
          !currentValue || currentValue === (previous[field] || "");
        if (followsHost && currentValue !== (value || "")) {
          details[field] = value || "";
          changed = true;
        }
      });
      return changed ? { ...current, details } : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncedKey]);
  const [rasterPreviewUrl, setRasterPreviewUrl] = useState("");

  // thumbnailRef - DEPRECATED: Only used by getCSSMeasurements() for fallback compatibility
  // Can be removed after full migration to ThumbnailSvgRenderer
  // const thumbnailRef = React.useRef(null);

  // NEW: Ref to the SVG renderer (single source of truth for preview and save)
  const svgRendererRef = React.useRef(null);
  const getSavePayloadRef = React.useRef(null);

  // DEPRECATED: getCSSMeasurements removed - no longer needed
  // ThumbnailSvgRenderer is now the single source of truth for positioning
  // All measurements come from THUMBNAIL_LAYOUT instead of DOM queries
  React.useImperativeHandle(
    ref,
    () => ({
      // Expose SVG element for rendering to canvas with font access
      getSvgElement: () => svgRendererRef.current?.getSvgElement?.(),
      // Expose SVG string for serialization/fallback
      getSvgString: () => svgRendererRef.current?.getSvgString?.(),
      getSavePayload: () => getSavePayloadRef.current?.(),
      setData: (data) => setState(mergeInitialData(data)),
    }),
    [],
  );

  // Surface unsaved edits so the parent can restore them if this unmounts.
  const onDraftChangeRef = React.useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;

  React.useEffect(() => {
    onDraftChangeRef.current(state);
  }, [state]);

  const iconMeta = ICON_META[state.selected];
  const iconColor = iconMeta?.color || DEFAULT_STATE.color;

  const previewBackground = useMemo(() => {
    if (state.useGradient) {
      const gradientColors = state.gradient.colors
        .map((entry) => normalizeHex(entry))
        .filter(Boolean);

      if (gradientColors.length >= 2) {
        return `linear-gradient(${
          state.gradient.angle
        }deg, ${gradientColors.join(", ")})`;
      }
    }

    if (state.useIconColor) {
      return iconColor;
    }

    return normalizeHex(state.color) || iconColor;
  }, [
    iconColor,
    state.color,
    state.gradient,
    state.useGradient,
    state.useIconColor,
  ]);

  const useDarkPreviewText = useMemo(() => {
    const testColors = state.useGradient
      ? state.gradient.colors
          .map((entry) => normalizeHex(entry))
          .filter(Boolean)
      : [
          state.useIconColor
            ? normalizeHex(iconColor)
            : normalizeHex(state.color) || normalizeHex(iconColor),
        ].filter(Boolean);

    if (testColors.length === 0) {
      return false;
    }

    const averageLuminance =
      testColors.reduce(
        (total, color) => total + getRelativeLuminance(color),
        0,
      ) / testColors.length;

    return averageLuminance >= 0.62;
  }, [
    iconColor,
    state.color,
    state.gradient.colors,
    state.useGradient,
    state.useIconColor,
  ]);

  const isThumbnailEmpty = useMemo(() => {
    const normalizeText = (value) => (value || "").trim().toUpperCase();

    const detailsMatchDefault = Object.keys(DEFAULT_STATE.details).every(
      (field) =>
        normalizeText(state.details[field]) ===
        normalizeText(DEFAULT_STATE.details[field]),
    );

    const currentGradientColors = [0, 1, 2].map(
      (index) => normalizeHex(state.gradient.colors?.[index]) || "",
    );
    const defaultGradientColors = [0, 1, 2].map(
      (index) => normalizeHex(DEFAULT_STATE.gradient.colors[index]) || "",
    );

    return (
      !state.selected &&
      normalizeText(state.year) === normalizeText(DEFAULT_STATE.year) &&
      state.useIconColor === DEFAULT_STATE.useIconColor &&
      state.useGradient === DEFAULT_STATE.useGradient &&
      (normalizeHex(state.color) || "") ===
        (normalizeHex(DEFAULT_STATE.color) || "") &&
      Number(state.gradient.angle) === Number(DEFAULT_STATE.gradient.angle) &&
      currentGradientColors.every(
        (color, index) => color === defaultGradientColors[index],
      ) &&
      detailsMatchDefault
    );
  }, [state]);

  // Helper function to get display value
  const getDisplayValue = (value) => {
    const normalized = (value || "").trim();
    if (!normalized || normalized.toUpperCase() === "N/A") {
      return "";
    }

    return normalized;
  };

  // Pre-calculate wrapped text that will be used in SVG (for consistent preview and rendering)
  const preCalculatedText = useMemo(() => {
    const creatorText = state.details.creatorSpeaker || "";
    const creatorFontString = createFontString(
      THUMBNAIL_LAYOUT.primary.fontSize,
      THUMBNAIL_LAYOUT.primary.fontWeight,
    );
    const creatorLines = wrapText(
      creatorText,
      THUMBNAIL_LAYOUT.primary.maxWidth,
      creatorFontString,
      THUMBNAIL_LAYOUT.primary.maxLines,
    );

    const titleText = state.details.title || "";
    const titleFontString = createFontString(
      THUMBNAIL_LAYOUT.title.fontSize,
      THUMBNAIL_LAYOUT.title.fontWeight,
    );
    const titleTruncated = truncateText(
      titleText,
      THUMBNAIL_LAYOUT.title.maxWidth,
      titleFontString,
    );
    const titleLines = [titleTruncated];

    const topicText = getDisplayValue(state.details.topic);
    const topicFontString = createFontString(
      THUMBNAIL_LAYOUT.secondary.fontSize,
      THUMBNAIL_LAYOUT.secondary.fontWeight,
    );
    // Only truncate if topic has a value, otherwise keep empty
    const topicTruncated = topicText
      ? truncateText(
          topicText,
          THUMBNAIL_LAYOUT.secondary.maxWidth,
          topicFontString,
        )
      : "";
    const topicLines = topicTruncated ? [topicTruncated] : [];

    const skuText = state.details.sku || "";
    const skuFontString = createFontString(
      THUMBNAIL_LAYOUT.sku.fontSize,
      THUMBNAIL_LAYOUT.sku.fontWeight,
    );
    const skuTruncated = truncateText(
      skuText,
      THUMBNAIL_LAYOUT.sku.maxWidth || THUMBNAIL_LAYOUT.primary.maxWidth,
      skuFontString,
    );
    const skuLines = [skuTruncated];

    const crValue =
      getDisplayValue(state.details.naCrNumber) ||
      getDisplayValue(state.details.anzCrNumber) ||
      "";
    const crFontString = createFontString(
      THUMBNAIL_LAYOUT.cr.fontSize,
      THUMBNAIL_LAYOUT.cr.fontWeight,
    );
    const crTruncated = truncateText(
      crValue,
      THUMBNAIL_LAYOUT.cr.maxWidth || THUMBNAIL_LAYOUT.primary.maxWidth,
      crFontString,
    );
    const crLines = [crTruncated];

    return {
      creatorLines,
      titleLines,
      topicLines,
      skuLines,
      crLines,
    };
  }, [
    state.details.creatorSpeaker,
    state.details.title,
    state.details.topic,
    state.details.sku,
    state.details.naCrNumber,
    state.details.anzCrNumber,
  ]);

  useEffect(() => {
    let isCurrent = true;

    const updateRasterPreview = async () => {
      const svgString = svgRendererRef.current?.getSvgString?.();
      if (!svgString) return;

      try {
        const nextPreviewUrl = await rasterizeSvg(svgString);
        if (isCurrent) {
          setRasterPreviewUrl(nextPreviewUrl);
        }
      } catch (error) {
        if (isCurrent) {
          setRasterPreviewUrl("");
        }
        console.error("ThumbnailBuilder: failed to rasterize preview", error);
      }
    };

    updateRasterPreview();

    return () => {
      isCurrent = false;
    };
  }, [previewBackground, preCalculatedText, state]);

  // NEW IMPLEMENTATION: Use ThumbnailSvgRenderer as single source of truth
  // getSvgString() ensures preview and saved SVG are pixel-identical
  const getSavePayload = async () => {
    try {
      if (!svgRendererRef.current) {
        console.error(
          "ThumbnailBuilder.getSavePayload: svgRendererRef is not available",
        );
        return null;
      }

      // Get SVG string directly from renderer
      const svgString = svgRendererRef.current.getSvgString();
      if (!svgString) {
        console.error(
          "ThumbnailBuilder.getSavePayload: Failed to generate SVG string",
        );
        return null;
      }

      const savedRasterPreviewUrl = await rasterizeSvg(svgString);

      return {
        ...state,
        previewBackground,
        useDarkPreviewText,
        isThumbnailEmpty,
        rasterPreviewUrl: savedRasterPreviewUrl,
        svgString, // Pass SVG string directly to save handler
      };
    } catch (error) {
      console.error("Error in ThumbnailBuilder.getSavePayload:", error);
      throw error;
    }
  };
  getSavePayloadRef.current = getSavePayload;

  const previewYear = getDisplayValue(state.year);

  return (
    <section className={classNames(styles.card, className)}>
      <div className={styles.inner}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.title}>Thumbnail Image</h2>
          <div className={styles.divider} />
        </div>

        <div className={styles.previewRow}>
          {/* Keep the SVG as the source, but show the same raster format that is uploaded. */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div className={styles.thumbnailPreview}>
              {rasterPreviewUrl ? (
                <img
                  src={rasterPreviewUrl}
                  alt="Thumbnail preview"
                  className={classNames(styles.rasterThumbnail, {
                    [styles.rasterThumbnailNoBorder]: state.selected,
                  })}
                />
              ) : null}
              <div className={styles.hiddenSvgSource} aria-hidden="true">
                <ThumbnailSvgRenderer
                  ref={svgRendererRef}
                  state={state}
                  preCalculatedText={preCalculatedText}
                  previewBackground={previewBackground}
                  useDarkPreviewText={useDarkPreviewText}
                />
              </div>
            </div>
          </div>

          <div className={styles.actionButtons}>
            <button
              className={styles.deleteButton}
              onClick={onDelete}
              aria-label="Delete thumbnail"
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          </div>
        </div>

        <IconPicker
          selected={state.selected}
          year={state.year}
          onChange={(selected) =>
            setState((current) => ({ ...current, selected }))
          }
          onYearChange={(year) => setState((current) => ({ ...current, year }))}
        />

        <ThumbnailColorPicker
          useIconColor={state.useIconColor}
          onUseIconColorChange={(useIconColor) =>
            setState((current) => ({ ...current, useIconColor }))
          }
          color={state.color}
          onColorChange={(color) =>
            setState((current) => ({ ...current, color }))
          }
          useGradient={state.useGradient}
          onUseGradientChange={(useGradient) =>
            setState((current) => ({ ...current, useGradient }))
          }
          gradient={state.gradient}
          onGradientChange={(gradient) =>
            setState((current) => ({ ...current, gradient }))
          }
        />

        <ThumbnailDetailsForm
          title={state.details.title}
          creatorSpeaker={state.details.creatorSpeaker}
          topic={state.details.topic}
          sku={state.details.sku}
          naCrNumber={state.details.naCrNumber}
          anzCrNumber={state.details.anzCrNumber}
          fields={detailFields}
          readOnlyFields={readOnlyDetailFields}
          readOnlyHint={readOnlyDetailsHint}
          onChange={(details) => {
            setState((current) => ({ ...current, details }));
            onDetailsChange(details);
          }}
        />
      </div>
    </section>
  );
});

ThumbnailBuilder.propTypes = {
  initialData: PropTypes.shape({
    selected: PropTypes.oneOf(Object.keys(ICON_META)),
    year: PropTypes.string,
    useIconColor: PropTypes.bool,
    color: PropTypes.string,
    useGradient: PropTypes.bool,
    gradient: PropTypes.shape({
      angle: PropTypes.number,
      colors: PropTypes.arrayOf(PropTypes.string),
    }),
    details: PropTypes.shape({
      title: PropTypes.string,
      creatorSpeaker: PropTypes.string,
      topic: PropTypes.string,
      sku: PropTypes.string,
      naCrNumber: PropTypes.string,
      anzCrNumber: PropTypes.string,
    }),
    title: PropTypes.string,
    creatorSpeaker: PropTypes.string,
    topic: PropTypes.string,
    sku: PropTypes.string,
    naCrNumber: PropTypes.string,
    anzCrNumber: PropTypes.string,
  }),
  onDelete: PropTypes.func,
  onDetailsChange: PropTypes.func,
  onDraftChange: PropTypes.func,
  hasExistingSvg: PropTypes.bool,
  className: PropTypes.string,
  detailFields: PropTypes.arrayOf(PropTypes.string),
  readOnlyDetailFields: PropTypes.arrayOf(PropTypes.string),
  readOnlyDetailsHint: PropTypes.string,
  syncedDetails: PropTypes.object,
};

ThumbnailBuilder.defaultProps = {
  initialData: null,
  onDelete: () => {},
  onDetailsChange: () => {},
  onDraftChange: () => {},
  hasExistingSvg: false,
  className: undefined,
  detailFields: undefined,
  readOnlyDetailFields: undefined,
  readOnlyDetailsHint: undefined,
  syncedDetails: null,
};

export default ThumbnailBuilder;
